import { Interpolation, SerializedStyles } from '@emotion/react';
import { lighten, readableColorIsBlack, toHex } from 'color2k';
import { replaceColor } from 'lottie-colorify';
import { useMemo, useRef } from 'react';
import { useInView } from '~/shared/hooks';
import { grey20, pureWhite } from '~/theme/lib/colors/colorsBase';
import { palette } from '~/theme/lib/colors/colorsKay';
import { roundTo } from '../../util/numbers';
import { randomFromList, randomWithin } from '../../util/random';
import { RandomLottieAnimation } from '../RandomLottieAnimation/RandomLottieAnimation';
import kayWood01 from './lottie/kay-wood-01.json';
import { StyledKayWood } from './styled';

const ANIMATION_DEFAULT_COLOR = '#DDD3A9';

// Array of animations to choose from (for variety)
const ANIMATIONS = [
	{
		...kayWood01,
		scale: 1,
	},
	// Construct a few variants from kayWood01 by hiding a few of the outer wood rings
	// Added custom scale param to all animations to account for the reduced size of the graphic
	{
		...kayWood01,
		nm: 'kay-wood-02',
		scale: 1.2,
		layers: [
			// Remove ring 7 and 8 (2 outermost rings)
			...kayWood01.layers.filter(({ nm }) => !nm.includes('ring_7') && !nm.includes('ring_8')),
		],
	},
	{
		...kayWood01,
		nm: 'kay-wood-03',
		scale: 1.3,
		layers: [
			// Remove ring 6, 7 and 8 (3 outermost rings)
			...kayWood01.layers.filter(({ nm }) => !nm.includes('ring_6') && !nm.includes('ring_7') && !nm.includes('ring_8')),
		],
	},
];

// Array of color pairs defined by design
const PREDEFINED_COLORS = {
	transparent: grey20,
	[toHex(pureWhite)]: grey20,
	[toHex(palette.white)]: grey20,
	[toHex(palette.orange100)]: palette.orange40,
	[toHex(palette.orange40)]: palette.orange100,
	[toHex(palette.green100)]: palette.green40,
	[toHex(palette.green40)]: palette.green20,
	[toHex(palette.green20)]: palette.green40,
	[toHex(palette.blue100)]: palette.blue40,
	[toHex(palette.blue40)]: palette.blue20,
	[toHex(palette.blue20)]: palette.blue40,
	[toHex(palette.burntRed100)]: palette.burntRed40,
	[toHex(palette.burntRed40)]: palette.burntRed20,
	[toHex(palette.burntRed20)]: palette.burntRed40,
	[toHex(palette.okker100)]: palette.okker40,
	[toHex(palette.okker40)]: palette.okker20,
	[toHex(palette.okker20)]: palette.okker40,
	[toHex(palette.sand100)]: palette.sand40,
	[toHex(palette.sand40)]: palette.sand20,
	[toHex(palette.sand20)]: palette.sand40,
};

export type KayWoodProps = {
	/**
	 * Which color the wood should accompany. The effect will not use this color directly itself, but pick a proper complementary shade.
	 */
	colorToComplement?: string;

	/**
	 * Set top css position
	 */
	top?: string | number;

	/**
	 * Set bottom css position
	 */
	bottom?: string | number;

	/**
	 * Set left css position
	 */
	left?: string | number;

	/**
	 * Set right css position
	 */
	right?: string | number;

	/**
	 * Define the level of random positioning.
	 *
	 * Either a single value, or an object with `x` and `y` properties to configure randomization of each axis individually.
	 */
	randomizePosition?: number | { x: number; y: number };

	/**
	 * Override built-in styling of component root with emotion CSS
	 *
	 * Example:
	 * `css={{ overflow: 'visible'}}`
	 */
	css?: SerializedStyles | Interpolation<any>;
};

/**
 * Will position itself absolute within container element, so keep that in mind.
 *
 * Position is determined by the `anchor` prop
 */
export const KayWood = ({ randomizePosition = 0.1, colorToComplement, ...restProps }: KayWoodProps) => {
	const ref = useRef<HTMLDivElement | null>(null);

	const color = useMemo(() => {
		if (colorToComplement && colorToComplement !== 'none') {
			const colorToComplementHex = toHex(colorToComplement);

			// If a predefined color for colorToComplement is found, use that
			if (PREDEFINED_COLORS[colorToComplementHex]) {
				return toHex(PREDEFINED_COLORS[colorToComplementHex]);
			}

			// Else lighten or darken based on whether colorToComplement is dark or light itself
			const lightenOrDarken = readableColorIsBlack(colorToComplementHex) ? -1 : 1;
			return toHex(lighten(colorToComplementHex, lightenOrDarken * 0.2));
		}
		return toHex(PREDEFINED_COLORS.transparent);
	}, [colorToComplement]);

	const randomAnimationPick = useMemo(() => randomFromList(ANIMATIONS), []);

	const lottieAnimation = useMemo(() => {
		// Finally return animation with replaced color
		return replaceColor(ANIMATION_DEFAULT_COLOR, toHex(color), randomAnimationPick) as (typeof ANIMATIONS)[0];
	}, [randomAnimationPick, color]);

	const style = useMemo(() => {
		const randomizeConfig = typeof randomizePosition === 'number' ? { x: randomizePosition, y: randomizePosition } : randomizePosition;

		const translateX = Math.round((randomWithin([randomizeConfig.x * -0.5, randomizeConfig.x * 0.5]) - 0.5) * 100);
		const translateY = Math.round((randomWithin([randomizeConfig.y * -0.5, randomizeConfig.y * 0.5]) - 0.5) * 100);

		const flipX = randomFromList([1, -1]);
		const flipY = randomFromList([1, -1]);
		const scale = [roundTo(randomWithin([0.6, 1]), 2) * flipX, roundTo(randomWithin([0.8, 1]), 2) * flipY];

		const rotate = Math.round(randomWithin([-20, 20]) + randomFromList([-180, 0, 180]));
		const skew = [roundTo(randomWithin([-5, 5]), 2), roundTo(randomWithin([-10, 10]), 2)];

		return {
			transform: `translate(${translateX}%, ${translateY}%) scale(${scale[0]}, ${scale[1]}) rotate(${rotate}deg) skew(${skew[0]}deg, ${skew[1]}deg)`,
		};
	}, [randomizePosition]);

	const { inView } = useInView(ref, { rootMargin: '-20%' }, true);

	return (
		<StyledKayWood
			ref={ref}
			style={style}
			scale={lottieAnimation.scale || 1}
			{...restProps}
		>
			<RandomLottieAnimation
				isPlaying={inView}
				animationList={[lottieAnimation]}
				modifyColor={{
					source: ANIMATION_DEFAULT_COLOR,
					target: color,
				}}
				renderer="canvas"
				autoplay={true}
				loop={false}
				className="lottie"
			/>
		</StyledKayWood>
	);
};
